// Super Crush pack editing helpers (mirror backend PackIn validation in routes_admin.py).
export const PACK_GROUPS = [
  { key: "free", title: "Free", hint: "Members on the Free tier" },
  { key: "single_lane", title: "Crushr / Winkr", hint: "Members on Crushr or Winkr" },
  { key: "duo", title: "Duo", hint: "Members on Duo" },
];

let seq = 0;
export const toRow = (p) => ({
  k: p.id || `new-${++seq}`, id: p.id || null, tier_group: p.tier_group,
  quantity: String(p.quantity ?? ""), price: p.price_usd != null ? Number(p.price_usd).toFixed(2) : "",
  store_product_id: p.store_product_id || "", active: p.active !== false, order: String(p.order ?? 0), badge: p.badge || "",
});

// Pack card highlight in the app ("" = none; wording lives in Content).
export const PACK_BADGES = [["", "None"], ["popular", "Popular"], ["best_value", "Best value"]];
const badgeLabel = (b) => PACK_BADGES.find(([k]) => k === (b || ""))?.[1] || b;

export function newRow(group, rows) {
  const mine = rows.filter((r) => r.tier_group === group);
  const qty = Math.min(100, Math.max(0, ...mine.map((r) => +r.quantity || 0)) + 5);
  const order = Math.min(100, Math.max(-1, ...mine.map((r) => +r.order || 0)) + 1);
  return toRow({ tier_group: group, quantity: qty, price_usd: null, order, active: true });
}

export function validateRows(rows) {
  const errors = {};
  const seen = {};
  rows.forEach((r) => {
    const e = {};
    if (!/^\d+$/.test(r.quantity) || +r.quantity < 1 || +r.quantity > 100) e.quantity = "1–100";
    if (!/^\d+(\.\d{1,2})?$/.test(r.price) || +r.price <= 0 || +r.price > 999.99) e.price = "0.01–999.99";
    if (r.store_product_id.trim().length > 120) e.store_product_id = "Max 120 characters";
    if (!/^\d+$/.test(r.order) || +r.order > 100) e.order = "0–100";
    const key = `${r.tier_group}:${+r.quantity}`;
    if (!e.quantity && seen[key]) { e.quantity = "Duplicate quantity"; errors[seen[key]] = { ...errors[seen[key]], quantity: "Duplicate quantity" }; }
    seen[key] = r.k;
    if (Object.keys(e).length) errors[r.k] = { ...errors[r.k], ...e };
  });
  return errors;
}

export const toPayload = (rows) => rows.map((r) => ({
  tier_group: r.tier_group, quantity: +r.quantity, price_usd: Math.round(+r.price * 100) / 100,
  store_product_id: r.store_product_id.trim() || null, active: r.active, order: +r.order, badge: r.badge || null,
}));

const groupTitle = (g) => PACK_GROUPS.find((x) => x.key === g)?.title || g;
const money = (v) => `$${Number(v).toFixed(2)}`;

// Human-readable list of changes between the saved packs and the draft rows.
export function diffPacks(original, rows) {
  const byId = Object.fromEntries(original.map((p) => [p.id, p]));
  const out = [];
  rows.forEach((r) => {
    const label = `${groupTitle(r.tier_group)} · ${r.quantity || "?"} pack`;
    const o = r.id && byId[r.id];
    if (!o) { out.push(`Add ${label} at ${r.price ? money(r.price) : "?"}`); return; }
    const parts = [];
    if (+r.quantity !== o.quantity) parts.push(`quantity ${o.quantity} → ${r.quantity}`);
    if (Math.round(+r.price * 100) !== Math.round(o.price_usd * 100)) parts.push(`price ${money(o.price_usd)} → ${r.price ? money(r.price) : "?"}`);
    if ((r.store_product_id.trim() || null) !== (o.store_product_id || null)) parts.push(`store ID ${o.store_product_id || "none"} → ${r.store_product_id.trim() || "none"}`);
    if (r.active !== o.active) parts.push(r.active ? "turned on" : "turned off");
    if (+r.order !== o.order) parts.push(`order ${o.order} → ${r.order}`);
    if ((r.badge || null) !== (o.badge || null)) parts.push(`badge ${badgeLabel(o.badge)} → ${badgeLabel(r.badge)}`);
    if (parts.length) out.push(`${label}: ${parts.join(", ")}`);
  });
  const kept = new Set(rows.map((r) => r.id).filter(Boolean));
  original.filter((p) => !kept.has(p.id)).forEach((p) => out.push(`Remove ${groupTitle(p.tier_group)} · ${p.quantity} pack (${money(p.price_usd)})`));
  return out;
}

// Map FastAPI 422 detail (loc: ["body","super_crush_packs", i, field]) onto row keys.
export function serverRowErrors(err, rows) {
  if (!Array.isArray(err?.data)) return {};
  const field = { price_usd: "price" };
  return err.data.reduce((acc, d) => {
    const [, , i, f] = d.loc || [];
    const row = rows[i];
    if (row && f) acc[row.k] = { ...acc[row.k], [field[f] || f]: String(d.msg || "").replace(/^Value error, /, "") };
    return acc;
  }, {});
}
